import { useEffect, useRef, useState } from "react";

import { useBBASveaAuthenticationGate } from "@bokio/bank/src/bba/useBBASveaAuthenticationGate";
import { CounterBadge } from "@bokio/components/CounterBadge/CounterBadge";
import { FeedbackModal } from "@bokio/components/FeedbackModal/FeedbackModal";
import RenderRequest from "@bokio/components/RenderRequest/RenderRequest";
import { SignAndPayPopover } from "@bokio/components/SignAndPay/SignAndPayPopover/SignAndPayPopover";
import { readPaymentSigningProgressInfoFromLocalStorage } from "@bokio/components/SveaBankIdAuthModal/sveaPollingUtils";
import { TopBarButton } from "@bokio/components/TopBar/TopBarButton";
import { usePaymentContext } from "@bokio/contexts/PaymentContext/PaymentContext";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { NoticePopover } from "@bokio/designsystem/components/NoticePopover/NoticePopover";
import { useDeviceQuery } from "@bokio/elements/DeviceQuery/useDeviceQuery";
import { AnalyticsEventCategory, useMetric } from "@bokio/hooks/useMetric/useMetric";
import { useUserFeedback } from "@bokio/hooks/useUserFeedback/useUserFeedback";
import { UserPreferenceType, useUserPreference } from "@bokio/hooks/useUserPreference/useUserPreference";
import { GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { useFeatureAvailability } from "@bokio/mobile-web-shared/core/contexts/FeatureAvailabilityContext/FeatureAvailabilityContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { mergeClassNames } from "@bokio/utils/classes";

import type { FC } from "react";

import * as styles from "./bbaTopBarButton.scss";

interface BbaTopBarButtonProps {
	backOfficeMode?: boolean;
}

const BbaTopBarButtonInner: FC<BbaTopBarButtonProps> = ({ backOfficeMode }) => {
	const generalLang = GeneralLangFactory();
	const { companyInfo } = useCompanyInfo();
	const companyId = companyInfo.Id;
	const { isMobile } = useDeviceQuery();
	const [addMetric] = useMetric();
	const { currentUserPermission, signablePaymentCount } = usePaymentContext();

	const {
		setPreference: setQuickAccessNoticePopoverPreference,
		getPreferenceRequest: getQuickAccessNoticePopoverPreferenceRequest,
	} = useUserPreference(companyId, UserPreferenceType.BbaTopBarQuickAccessDismissed);
	const bbaQuickAccessNoticePopoverDismissedPref = getQuickAccessNoticePopoverPreferenceRequest.data?.Data;

	const { paymentValidationRulesRequest } = usePaymentContext();

	const [isQuickAccessNoticePopoverOpened, setIsQuickAccessNoticePopoverOpened] = useState(false);
	const iconRef = useRef<HTMLDivElement>(null);
	const popoverWrapperRef = useRef<HTMLDivElement>(null);

	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const { sendFeedback, sendFeedbackRequest } = useUserFeedback(() => {
		setShowFeedbackModal(false);
	});

	const [isPopoverOpened, setIsPopoverOpened] = useState(false);
	const closePopover = () => {
		setIsPopoverOpened(false);
	};

	const closeQuickAccessNoticePopover = () => {
		setIsQuickAccessNoticePopoverOpened(false);
		setQuickAccessNoticePopoverPreference({
			isCompanySpecific: true,
			value: { [UserPreferenceType.BbaTopBarQuickAccessDismissed]: { Value: true } },
		});
	};

	useEffect(() => {
		if (
			bbaQuickAccessNoticePopoverDismissedPref &&
			!bbaQuickAccessNoticePopoverDismissedPref.BbaTopBarQuickAccessDismissed?.Value
		) {
			setIsQuickAccessNoticePopoverOpened(true);
			return;
		}
	}, [bbaQuickAccessNoticePopoverDismissedPref]);

	/**
	 * This is used to check if the user already has a payment signing in progress. (Read more about the methods in sveaPollingUtils.ts). If a user starts a payment signing,
	 * and during the polling phase if a user refresh the page, we use the SignAndPayPopover to continue the same payment signing, hence we open it automatically when a payment
	 * signing process gets interuppted due to a page refresh.
	 * More Info on Slack thread: https://bokio.slack.com/archives/C01NX3ZMSE4/p1717053838598069
	 *  */
	useEffect(() => {
		if (readPaymentSigningProgressInfoFromLocalStorage()) {
			setIsPopoverOpened(true);
		}
	}, []);

	const renderBadge = () => {
		const renderCounterBadge = (c: string | number) => (
			<span className={styles.badgeWrapper}>
				<CounterBadge size="xsmall" border>
					{c}
				</CounterBadge>
			</span>
		);

		switch (true) {
			case currentUserPermission.CanSignPayment && signablePaymentCount > 0:
				return renderCounterBadge(signablePaymentCount > 9 ? "9+" : `${signablePaymentCount}`);
			default:
				return null;
		}
	};

	const renderPopoverContent = () => {
		return (
			<RenderRequest request={paymentValidationRulesRequest} whenLoading={null}>
				{paymentValidationRules => (
					<SignAndPayPopover
						toggleRef={popoverWrapperRef}
						testId="BbaTopBarButton_SignAndPayPopover"
						visible={isPopoverOpened}
						arrowAnchor={iconRef.current}
						onClose={closePopover}
						paymentValidationRules={paymentValidationRules}
					/>
				)}
			</RenderRequest>
		);
	};

	const renderNoticePopover = () => {
		return (
			<NoticePopover
				toggleRef={popoverWrapperRef}
				testIdOfCloseButton="BbaTopBarButton_NoticePopover"
				isOpen={isQuickAccessNoticePopoverOpened}
				message={generalLang.BBA_TopBarButton_NoticePopoverContent_QuickAccess}
				align={isMobile ? "middle" : "right"}
				direction={"down"}
				arrowAnchor={iconRef.current}
				onClose={reason => {
					if (reason === "close") {
						closeQuickAccessNoticePopover();
					}
				}}
			/>
		);
	};

	return (
		<div className={styles.container}>
			<div ref={popoverWrapperRef}>
				<TopBarButton
					className={mergeClassNames(styles.button)}
					testId="BBATopBarButton"
					onClick={() => {
						setIsPopoverOpened(isOpen => !isOpen);
						if (isQuickAccessNoticePopoverOpened) {
							closeQuickAccessNoticePopover();
						}
						addMetric(AnalyticsEventCategory.Bba, "ShowSignAndPayPopover");
					}}
				>
					<span ref={iconRef}>
						<Icon
							name="bba"
							size="28"
							color={backOfficeMode ? "lightGrey" : "darkGrey"}
							className={styles.buttonIcon}
						/>
					</span>
					{renderBadge()}
				</TopBarButton>

				{renderNoticePopover()}
				{renderPopoverContent()}
			</div>

			<FeedbackModal
				visible={showFeedbackModal}
				request={sendFeedbackRequest}
				onClose={() => setShowFeedbackModal(false)}
				onSendFeedback={text => {
					sendFeedback(text, "Feedback for Not Interested in BBA", "bba-onboarding");
				}}
			/>
		</div>
	);
};

export const BbaTopBarButton: FC<BbaTopBarButtonProps> = ({ backOfficeMode }) => {
	const { companyInfo } = useCompanyInfo();
	const featureAvailability = useFeatureAvailability();
	const gateState = useBBASveaAuthenticationGate();

	const baseCondition =
		featureAvailability.BokioBusinessAccount &&
		companyInfo &&
		companyInfo.CompanySystem === m.Entities.CompanySystem.Default;

	if (!baseCondition) {
		return null;
	}

	switch (gateState.type) {
		case "disabled":
			// SveaDelegates feature toggle is off â€” behave exactly as before
			return gateState.canSignAndPay ? <BbaTopBarButtonInner backOfficeMode={backOfficeMode} /> : null;
		case "notPermitted":
			return null;
		case "unauthenticated":
		case "authenticated":
			return <BbaTopBarButtonInner backOfficeMode={backOfficeMode} />;
	}
};
